import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AllDocumentTypes } from 'types';

export interface PendingAttachment {
  id: string;
  name: string[];
}

export interface FlaggingModeState {
  isActive: boolean;
  flagLabel: string;
  allowedDocumentTypes: AllDocumentTypes[];
  pendingAttachments: Record<number, PendingAttachment[]>;
  hasDecisionRendered: boolean;
  activeFlowIndex: number;
}

const initialState: FlaggingModeState = {
  isActive: false,
  flagLabel: '',
  allowedDocumentTypes: [],
  pendingAttachments: {},
  hasDecisionRendered: false,
  activeFlowIndex: 0,
};

export const flaggingModeSlice = createSlice({
  name: 'flaggingMode',
  initialState,
  reducers: {
    activateFlaggingMode(
      state,
      action: PayloadAction<{
        flagLabel: string;
        allowedDocumentTypes: AllDocumentTypes[];
        hasDecisionRendered: boolean;
      }>,
    ) {
      state.isActive = true;
      state.flagLabel = action.payload.flagLabel;
      state.allowedDocumentTypes = action.payload.allowedDocumentTypes;
      state.hasDecisionRendered = action.payload.hasDecisionRendered;
      state.pendingAttachments = {};
      state.activeFlowIndex = 0;
    },
    deactivateFlaggingMode(state) {
      state.isActive = false;
      state.flagLabel = '';
      state.allowedDocumentTypes = [];
      state.pendingAttachments = {};
      state.hasDecisionRendered = false;
      state.activeFlowIndex = 0;
    },
    setActiveFlowIndex(state, action: PayloadAction<number>) {
      state.activeFlowIndex = action.payload;
    },
    addAttachments(state, action: PayloadAction<PendingAttachment[]>) {
      const idx = state.activeFlowIndex;
      if (!state.pendingAttachments[idx]) {
        state.pendingAttachments[idx] = [];
      }
      const existingIds = new Set(state.pendingAttachments[idx].map((a) => a.id));
      for (const attachment of action.payload) {
        if (!existingIds.has(attachment.id)) {
          state.pendingAttachments[idx].push(attachment);
          existingIds.add(attachment.id);
        }
      }
    },
    removeAttachment(state, action: PayloadAction<string>) {
      const idx = state.activeFlowIndex;
      if (state.pendingAttachments[idx]) {
        state.pendingAttachments[idx] = state.pendingAttachments[idx].filter((a) => a.id !== action.payload);
      }
    },
  },
});

export const { activateFlaggingMode, deactivateFlaggingMode, setActiveFlowIndex, addAttachments, removeAttachment } =
  flaggingModeSlice.actions;

export default flaggingModeSlice.reducer;
